import { ROLES } from '@/shared/auth/constants'

import type { Role } from '@/shared/auth/types'

/**
 * Collector and log-source permissions, named after the backend's own
 * (airlib/airlib/auth/permissions.py) rather than invented here: the
 * frontend has no per-permission claims to check against, so this file is
 * the Role -> permission table that stands in for one.
 */
export const COLLECTOR_PERMISSIONS = {
	ReadCollectors: 'ReadCollectors',
	ReadAgentSettings: 'ReadAgentSettings',
	WriteAgentSettings: 'WriteAgentSettings',
	RunAgentDiagnostics: 'RunAgentDiagnostics',
	RunAgentCapture: 'RunAgentCapture',
	RestartAgent: 'RestartAgent',
	EnrollRemoteIngestionAgent: 'EnrollRemoteIngestionAgent',
} as const

export type CollectorPermission =
	(typeof COLLECTOR_PERMISSIONS)[keyof typeof COLLECTOR_PERMISSIONS]

const READ_ONLY_PERMISSIONS: ReadonlySet<CollectorPermission> = new Set([
	COLLECTOR_PERMISSIONS.ReadCollectors,
	COLLECTOR_PERMISSIONS.ReadAgentSettings,
])

const ANALYST_PERMISSIONS: ReadonlySet<CollectorPermission> = new Set([
	...READ_ONLY_PERMISSIONS,
	COLLECTOR_PERMISSIONS.RunAgentDiagnostics,
	COLLECTOR_PERMISSIONS.RunAgentCapture,
])

const FULL_PERMISSIONS: ReadonlySet<CollectorPermission> = new Set(
	Object.values(COLLECTOR_PERMISSIONS),
)

/**
 * The product rule, deliberately narrower than airlib's `SystemRoles`:
 * Analyst sees every collector and log source but acts only by running
 * diagnostics or a capture, though the backend also grants it restart,
 * enroll and write-settings. Everything else is System Manager and up.
 * Admin reaches every permission through the backend's wildcard
 * (`claims.py` `allow_admin`) — listed in full here rather than derived
 * from rank, since there is no wildcard to check on the frontend.
 */
const ROLE_PERMISSIONS: Record<Role, ReadonlySet<CollectorPermission>> = {
	[ROLES.Admin]: FULL_PERMISSIONS,
	[ROLES.SystemManager]: FULL_PERMISSIONS,
	[ROLES.Analyst]: ANALYST_PERMISSIONS,
	[ROLES.ReadOnly]: READ_ONLY_PERMISSIONS,
}

/**
 * Whether `role` carries `permission` — a permission-set check, not the role
 * rank comparison `hasRequiredRole` does. Analyst holds diagnostics and
 * capture without holding restart or the writes; a rank check cannot
 * express that without granting Analyst everything or nothing.
 */
export const roleHasCollectorPermission = (
	role: Role | null | undefined,
	permission: CollectorPermission,
): boolean => {
	if (!role) return false
	return ROLE_PERMISSIONS[role].has(permission)
}
